import type { EditorSDK } from '@wix/platform-editor-sdk';

import type { RouteConfiguration } from '../../../../types/controllers';
import { updateRoutePathInMenuItems } from '../../../wrappers/menus';
import type { IsSectionInstalledProps } from '../../types';
import { getRoutesFromAppData, setRoutesInAppData } from './app-data';
import { getRoutesFromGlobalController } from './controllers';

export const getRoutes = async (editorSDK: EditorSDK) => {
  const [routesInAppData, routesInGlobalController] = await Promise.all([
    getRoutesFromAppData(editorSDK),
    getRoutesFromGlobalController(editorSDK),
  ]);

  return routesInAppData ?? routesInGlobalController;
};

export const setRoutes = async (editorSDK: EditorSDK, routes: RouteConfiguration[]) => {
  await setRoutesInAppData(editorSDK, routes);
};

export const routeExists = async (editorSDK: EditorSDK, { widgetId }: IsSectionInstalledProps) => {
  const routes = await getRoutes(editorSDK);
  return routes.some((route) => route.widgetId === widgetId);
};

export const updateRoutePath = async (editorSDK: EditorSDK, widgetId: string, newPath: string) => {
  if (!newPath) {
    throw new Error('Path must not be empty');
  }
  if (!/^[a-zA-Z0-9\-_]+$/.test(newPath)) {
    throw new Error('Path must contain only alphanumeric characters, hyphens, or underscores');
  }

  const routes = await getRoutes(editorSDK);
  const currentRoute = routes.find((r) => r.widgetId === widgetId);
  const oldPath = currentRoute?.path;

  const isDuplicate = !!routes.find((r) => r.path === newPath && r.widgetId !== widgetId);
  if (isDuplicate) {
    throw new Error(`Path "${newPath}" is already used by another route`);
  }

  const updatedRoutes = routes.map((route) =>
    route.widgetId === widgetId ? { ...route, path: newPath } : route,
  );
  await setRoutes(editorSDK, updatedRoutes);

  if (oldPath && oldPath !== newPath) {
    await updateRoutePathInMenuItems({ editorSDK, widgetId, oldPath, newPath });
  }
};
